'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/lang-extra'

const Word2VecTrainer = dynamic(() => import('../_components/widgets/SequenceWidgets').then(m => m.Word2VecTrainer), { ssr: false })

export default function Word2Vec() {
  return (
    <Lesson>
      <Step>
        <H>Where do embeddings come from?</H>
        <P>
          Last lesson, words were points in space with similar meanings close together. But who decides where “cat” goes?
          Nobody writes those numbers by hand. They’re <em>learned</em>, from a simple observation by the linguist J. R. Firth:
          “You shall know a word by the company it keeps.”
        </P>
        <P>
          “Cat” and “dog” turn up in the same sorts of sentences: “the ___ sleeps”, “a little ___ runs”. “Bread” turns up
          somewhere else: “she eats the ___”. So start by simply counting which words appear near which.
        </P>
        <Exercise
          id="cooccurrence"
          title="counts and similar"
          {...EX.cooccurrence}
          hints={[
            <>
              For every position <C>i</C> in every sentence, loop <C>j</C> from <C>i − window</C> to <C>i + window</C> (inside
              the sentence) and, when <C>{'j !== i'}</C>, add 1 to <C>{'C[id.get(s[i])!][id.get(s[j])!]'}</C>.
            </>,
            <>
              Sort the other words by <C>{'vec.cosine(C[id.get(b)!], row)'}</C>, biggest first, and take <C>k</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-cooc">
        <H>Counting doesn’t scale</H>
        <P>
          It works. Animals have similar rows, foods have similar rows. But a real vocabulary has 50 000 words or more, so the
          table has 2.5 billion entries, almost all zero. We want short, dense vectors instead, of a few hundred numbers each,
          that keep the same “similar company, similar vector” property.
        </P>
        <Quiz
          id="q-cooc"
          question="Why do “cat” and “dog” end up with similar rows in the counts table?"
          options={[
            { label: 'They appear next to the same kinds of words', correct: true },
            { label: 'They often appear next to each other', why: 'In our sentences they rarely appear together at all. What they share is their neighbours.' },
            { label: 'They’re both three letters long', why: 'The counts never look at spelling, only at which words are nearby.' },
          ]}
        />
      </Step>

      <Step>
        <H>word2vec: learn the vectors directly</H>
        <P>
          In 2013, Tomas Mikolov’s team at Google turned this into a tiny learning problem. Give every word a small random
          vector. Then repeat:
        </P>
        <UL>
          <li>
            Take a real pair, a word and a neighbour. Nudge their vectors so their dot product goes up.
          </li>
          <li>
            Take the word and a few <em>random</em> words (“negative samples”). Nudge those dot products down.
          </li>
        </UL>
        <MB>{'L = -\\log \\sigma(\\mathbf{u} \\cdot \\mathbf{v}_{\\text{near}}) \\qquad - \\sum_{\\text{neg}} \\log \\sigma(-\\mathbf{u} \\cdot \\mathbf{v}_{\\text{neg}})'}</MB>
        <P>
          Here <M>{'\\mathbf{u}'}</M> is the word’s vector and <M>{'\\mathbf{v}'}</M> a neighbour’s. Words used in similar
          company get pulled towards the same neighbours, so they drift together. Watch it happen:
        </P>
        <Figure n="8.4" wide caption="Every word starts at a random point. Press Train: real neighbours pull together, random pairs push apart, and the categories separate on their own. (8-D vectors, shown on their top two principal components.)">
          <Word2VecTrainer />
        </Figure>
      </Step>

      <Step gate="sgns">
        <H>Write the update</H>
        <P>
          The gradient is the logistic-regression one you already know. The prediction is <M>{'\\sigma(\\mathbf{u}\\cdot\\mathbf{v})'}</M>{' '}
          and the label is 1 or 0, so the error is <M>{'g = \\sigma(\\mathbf{u}\\cdot\\mathbf{v}) - \\text{label}'}</M>. Each
          vector moves by <M>g</M> times the other.
        </P>
        <Exercise
          id="sgns"
          title="update"
          {...EX.sgns}
          hints={[
            <>
              <C>{'const g = 1 / (1 + Math.exp(-vec.dot(inp, out))) - label'}</C>
            </>,
            <>
              In the loop: <C>{'const a = inp[k]; inp[k] -= lr * g * out[k]; out[k] -= lr * g * a'}</C>
            </>,
          ]}
        />
      </Step>

      <Step gate="q-bias">
        <H>What embeddings learn — including what we don’t want</H>
        <P>
          Trained on billions of words, word2vec’s vectors captured remarkable structure: countries near their capitals, verb
          tenses lined up, and the famous <M>{'\\text{king} - \\text{man} + \\text{woman} \\approx \\text{queen}'}</M>.
        </P>
        <Quiz
          id="q-bias"
          question="Embeddings trained on news articles placed “nurse” closer to “woman” and “engineer” closer to “man”. Why?"
          options={[
            { label: 'They faithfully learn the patterns in the text — including society’s stereotypes', correct: true },
            { label: 'A programmer added those associations', why: 'Nobody wrote them in. The training only ever looks at which words appear together.' },
            { label: 'It’s random noise', why: 'It shows up reliably across datasets and methods, so it isn’t noise.' },
          ]}
          explain="A model learns whatever is in its data, good and bad. That’s why the data behind a model, and how it’s checked, matters so much (more in Module 10)."
        />
        <Callout kind="why">
          Modern language models don’t use word2vec. They learn their embedding table as part of the whole network, by
          gradient descent on next-token prediction, exactly like the MLP in the next lesson. But the core idea is the same:
          meaning from context.
        </Callout>
      </Step>
    </Lesson>
  )
}
